import { useFormContext } from 'react-hook-form';
import { useLocale } from 'shared-ui';
import { useEffect, useCallback } from 'react';
import { useUpdateEffect } from 'react-use';

import { AppMessages } from 'i18n/messages';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import * as Styled from '../UserPermissions.styles';
import { UserPermissionsForm } from '../UserPermissions.types';
import { useSelectedUser } from 'hooks/useSelectedUser/useSelectedUser';
import { useAuth } from 'hooks/useAuth/useAuth';
import { usePermissions } from 'hooks/usePermissions/usePermissions';

import { PermissionHeader } from './permissionHeader/PermissionHeader';
import { PermissionRow } from './permissionRow/PermissionRow';
import { PermissionOptions } from './permissionRow/PermissionRow.types';
import { SectionHeader } from './sectionHeader/SectionHeader';

export const EDIT_ONLY_FUNCTIONALITIES = [
  Functionality.KOMUNIKAT,
  Functionality.DEFINICJE,
  Functionality.UZYTKOWNICY,
  Functionality.HISTORIA,
  Functionality.ARCHIWUM,
  Functionality.LICENCJA,
  Functionality.UPR_GRUPOWE,
  Functionality.ZDARZENIA_INNE,
  Functionality.ZESTAWIENIE_UPRAWNIEN,
  Functionality.RAPORT_WYDANYCH_KART,
  Functionality.RAPORT_POSIADACZY_BEZ_ZDARZEN,
  Functionality.RAPORT_ZDARZEN,
  Functionality.RAPORT_ZDARZEN_INNE,
  Functionality.RAPORT_CZASU_OBECNOSCI,
  Functionality.OBECNOSCI,
  Functionality.RAPORT_OSOB_OBECNYCH,
];

export const UserFunctionalPermissions = () => {
  const { formatMessage } = useLocale();
  const { watch, setValue } = useFormContext<UserPermissionsForm>();
  const permissions = watch('permissions');
  const { selectedUser } = useSelectedUser();
  const { user } = useAuth();
  const { perms } = usePermissions();

  const getPermission = useCallback(
    (functionality: Functionality) => {
      return permissions.find((p) => p.functionality === functionality);
    },
    [permissions],
  );

  const urzadzeniaGlc = getPermission(Functionality.URZADZENIA_GLC);
  const kartyPosiadacze = getPermission(Functionality.KARTY_POSIADACZE);
  const komunikat = getPermission(Functionality.KOMUNIKAT);
  const uzytkownicy = getPermission(Functionality.UZYTKOWNICY);

  const harmonogramyDisabled = !urzadzeniaGlc?.viewAction;
  const cardDependantDisabled = !kartyPosiadacze?.viewAction;
  const cardDependantModifyDisabled = !kartyPosiadacze?.modifyAction;
  const uprGrupoweDisabled = cardDependantDisabled || cardDependantModifyDisabled;
  const historiaDisabled =
    !urzadzeniaGlc?.viewAction && !kartyPosiadacze?.viewAction && !komunikat?.viewAction && !uzytkownicy?.viewAction;
  const archiwumDisabled = !urzadzeniaGlc?.viewAction && !kartyPosiadacze?.viewAction;
  const komunikatDisabled = !urzadzeniaGlc?.viewAction && !kartyPosiadacze?.viewAction;
  const isCurrentUser = selectedUser?.id === user?.id;

  useEffect(() => {
    EDIT_ONLY_FUNCTIONALITIES.forEach((func) => {
      const index = permissions.findIndex((p) => p.functionality === func);
      if (index !== -1) {
        setValue(`permissions.${index}.modifyAction`, true, { shouldDirty: true });
      }
    });
  }, [permissions, setValue]);

  useEffect(() => {
    const dependentPermissions = [
      { func: Functionality.HARMONOGRAMY, disabled: harmonogramyDisabled },
      { func: Functionality.UPR_INDYWIDUALNE, disabled: cardDependantDisabled },
      { func: Functionality.KONTROLA, disabled: cardDependantDisabled },
      { func: Functionality.POSZUKIWANY, disabled: cardDependantDisabled },
      { func: Functionality.SEKWENCJA, disabled: cardDependantDisabled },
      { func: Functionality.ZDARZENIA, disabled: cardDependantDisabled },
      { func: Functionality.UPR_GRUPOWE, disabled: cardDependantDisabled },
      { func: Functionality.ZDARZENIA_INNE, disabled: cardDependantDisabled },
      { func: Functionality.OBECNOSCI, disabled: cardDependantDisabled },
      { func: Functionality.HISTORIA, disabled: historiaDisabled },
      { func: Functionality.ARCHIWUM, disabled: archiwumDisabled },
      { func: Functionality.KOMUNIKAT, disabled: komunikatDisabled },
    ];

    dependentPermissions.forEach(({ func, disabled }) => {
      if (disabled) {
        const index = permissions.findIndex((p) => p.functionality === func);
        if (index !== -1) {
          if (getPermission(func)?.viewAction) {
            setValue(`permissions.${index}.viewAction`, false, { shouldDirty: true });
            if (!EDIT_ONLY_FUNCTIONALITIES.includes(func)) {
              setValue(`permissions.${index}.modifyAction`, false, { shouldDirty: true });
            }
          }
        }
      }
    });
  }, [
    permissions,
    setValue,
    getPermission,
    harmonogramyDisabled,
    cardDependantDisabled,
    uprGrupoweDisabled,
    historiaDisabled,
    archiwumDisabled,
  ]);

  useUpdateEffect(() => {
    if (!cardDependantDisabled) {
      const cardDependentPermissions = [
        Functionality.UPR_INDYWIDUALNE,
        Functionality.KONTROLA,
        Functionality.POSZUKIWANY,
        Functionality.SEKWENCJA,
        Functionality.ZDARZENIA,
        Functionality.UPR_GRUPOWE,
        Functionality.ZDARZENIA_INNE,
        Functionality.OBECNOSCI,
      ];

      cardDependentPermissions.forEach((func) => {
        const index = permissions.findIndex((p) => p.functionality === func);
        if (index !== -1) {
          if (!getPermission(func)?.viewAction) {
            setValue(`permissions.${index}.viewAction`, true, { shouldDirty: true });
            if (!EDIT_ONLY_FUNCTIONALITIES.includes(func)) {
              setValue(`permissions.${index}.modifyAction`, true, { shouldDirty: true });
            }
          }
        }
      });
    }
  }, [cardDependantDisabled]);

  useEffect(() => {
    if (cardDependantModifyDisabled) {
      const dependentModifyPermissions = [
        Functionality.UPR_INDYWIDUALNE,
        Functionality.KONTROLA,
        Functionality.POSZUKIWANY,
        Functionality.SEKWENCJA,
        Functionality.ZDARZENIA,
      ];

      dependentModifyPermissions.forEach((func) => {
        const index = permissions.findIndex((p) => p.functionality === func);
        if (index !== -1 && getPermission(func)?.modifyAction) {
          setValue(`permissions.${index}.modifyAction`, false, { shouldDirty: true });
        }
      });
    }
  }, [cardDependantModifyDisabled, getPermission, permissions, setValue]);

  return (
    <Styled.Form>
      <Styled.Container>
        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.devices'] })}
            functionality={Functionality.URZADZENIA_GLC}
          />
          <PermissionRow
            functionality={Functionality.HARMONOGRAMY}
            title={formatMessage({ id: AppMessages['permissions.functionality.schedules'] })}
            options={PermissionOptions.FULL}
            disabled={harmonogramyDisabled}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.cards'] })}
            functionality={Functionality.KARTY_POSIADACZE}
          />
          <PermissionRow
            functionality={Functionality.UPR_INDYWIDUALNE}
            title={formatMessage({ id: AppMessages['permissions.functionality.individualPermissions'] })}
            options={PermissionOptions.FULL}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.KONTROLA}
            title={formatMessage({ id: AppMessages['permissions.functionality.control'] })}
            options={PermissionOptions.FULL}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.POSZUKIWANY}
            title={formatMessage({ id: AppMessages['permissions.functionality.wanted'] })}
            options={PermissionOptions.FULL}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.SEKWENCJA}
            title={formatMessage({ id: AppMessages['permissions.functionality.sequences'] })}
            options={PermissionOptions.FULL}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.UPR_GRUPOWE}
            title={formatMessage({ id: AppMessages['permissions.functionality.groupPermissions'] })}
            options={PermissionOptions.EDIT_ONLY}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.ZDARZENIA}
            title={formatMessage({ id: AppMessages['permissions.functionality.events'] })}
            options={PermissionOptions.FULL}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.ZDARZENIA_INNE}
            title={formatMessage({ id: AppMessages['permissions.functionality.eventsOther'] })}
            options={PermissionOptions.EDIT_ONLY}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
          <PermissionRow
            functionality={Functionality.OBECNOSCI}
            title={formatMessage({ id: AppMessages['permissions.functionality.presence'] })}
            options={PermissionOptions.EDIT_ONLY}
            disabled={cardDependantDisabled}
            modifyDisabled={cardDependantModifyDisabled}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.announcements'] })}
            functionality={Functionality.KOMUNIKAT}
            disabled={komunikatDisabled}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.definitions'] })}
            functionality={Functionality.DEFINICJE}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.users'] })}
            functionality={Functionality.UZYTKOWNICY}
            disabled={isCurrentUser || !perms?.UZYTKOWNICY?.edit}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.history'] })}
            functionality={Functionality.HISTORIA}
            disabled={historiaDisabled}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.archive'] })}
            functionality={Functionality.ARCHIWUM}
            disabled={archiwumDisabled}
          />
        </Styled.Section>

        <Styled.Section>
          <SectionHeader title={formatMessage({ id: AppMessages['permissions.section.reports'] })} />

          <PermissionRow
            functionality={Functionality.ZESTAWIENIE_UPRAWNIEN}
            title={formatMessage({ id: AppMessages['permissions.functionality.permissionsSummary'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_WYDANYCH_KART}
            title={formatMessage({ id: AppMessages['permissions.functionality.issuedCardsReport'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_POSIADACZY_BEZ_ZDARZEN}
            title={formatMessage({ id: AppMessages['permissions.functionality.ownersWithoutEventsReport'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_ZDARZEN}
            title={formatMessage({ id: AppMessages['permissions.functionality.eventsReport'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_ZDARZEN_INNE}
            title={formatMessage({ id: AppMessages['permissions.functionality.eventsReportOther'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_CZASU_OBECNOSCI}
            title={formatMessage({ id: AppMessages['permissions.functionality.timeOfOwnershipReport'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
          <PermissionRow
            functionality={Functionality.RAPORT_OSOB_OBECNYCH}
            title={formatMessage({ id: AppMessages['permissions.functionality.presentPersonsReport'] })}
            options={PermissionOptions.EDIT_ONLY}
          />
        </Styled.Section>

        <Styled.Section>
          <PermissionHeader
            title={formatMessage({ id: AppMessages['permissions.section.license'] })}
            functionality={Functionality.LICENCJA}
          />
        </Styled.Section>
      </Styled.Container>
    </Styled.Form>
  );
};
